import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Avatar from '@material-ui/core/Avatar';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';

import { useAuthContext } from '../../web-app-components/components/AuthProvider';
import axiosInstance from '../../utils/axiosInstance';
import endpoints from '../../utils/endpoints';
import appVersionClient from '../../clients/app-version';
import { REGIONS } from '../Objects/components/Form/components/RegionSelectField';

import StripePaymentForm from './StripePaymentForm';

const getRegionLabel = (value) => {
  if (!value) return '';
  const region = REGIONS.find((r) => r.value === value);
  return region ? region.label : value;
};

const TRAVEL_METHOD_LABELS = {
  'lt-LT': {
    pėsčiomis: 'Pėsčiomis',
    dviračiu: 'Dviračiu',
    automobiliu: 'Automobiliu',
    visureigiu: 'Visureigiu',
    Street: '"Street"',
    Enduro: '"Enduro"',
  },
  'en-EU': {
    pėsčiomis: 'On foot',
    dviračiu: 'By bike',
    automobiliu: 'By car',
    visureigiu: 'All-terrain',
    Street: '"Street"',
    Enduro: '"Enduro"',
  },
};

const TRAVEL_METHOD_ICONS = {
  pėsčiomis: 'pesciomis.png',
  dviračiu: 'dviraciu.png',
  automobiliu: 'automobiliu.png',
  visureigiu: 'Visureigiu.png',
  Street: 'street.png',
  Enduro: 'Enduro.png',
};

const SUITABLE_FOR_LABELS = {
  'lt-LT': {
    solo: 'Solo',
    pora: 'Pora',
    šeima: 'Šeima',
    grupė: 'Grupė',
  },
  'en-EU': {
    solo: 'Solo',
    pora: 'Pair',
    šeima: 'Family',
    grupė: 'Group',
  },
};

const GREEN = '#4EA14C';
const DARK_GREEN = '#20441E';
const ELECTRIC_BLUE = 'rgba(94, 230, 255, 0.1)';
const DARK_ELECTRIC_BLUE = 'rgba(10, 94, 120, 0.1)';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    margin: 0,
    padding: 0,
    backgroundColor: '#081A1B',
    backgroundImage:
      'radial-gradient(circle at top right, #204F51 0%, #081A1B 80%)',
    color: '#fff',
    boxSizing: 'border-box',
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  },
  content: {
    maxWidth: 460,
    margin: '0 auto',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    boxSizing: 'border-box',
  },
  contentScrollArea: {
    paddingBottom: theme.spacing(2),
  },
  infoCard: {
    background: 'rgba(22, 34, 35, 0.78)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: 24,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.28)',
    padding: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(2),
    color: '#fff',
  },
  price: {
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: 1.2,
    marginTop: theme.spacing(2),
    color: '#fff',
  },
  buyButton: {
    marginTop: theme.spacing(2),
    backgroundColor: GREEN,
    backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
    color: '#fff',
    borderRadius: 28,
    '&:hover': {
      backgroundColor: GREEN,
      backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
      filter: 'brightness(0.95)',
    },
  },
  discountApplyButton: {
    backgroundColor: GREEN,
    backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
    color: '#fff !important',
    borderRadius: 28,
    border: 0,
    '&:hover': {
      backgroundColor: GREEN,
      backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
      color: '#fff !important',
      filter: 'brightness(0.95)',
      border: 0,
    },
  },
  textSecondary: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  alreadyPurchased: {
    color: GREEN,
    marginTop: 16,
  },
  navigateBackButton: {
    marginTop: theme.spacing(2),
    backgroundColor: GREEN,
    backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
    color: '#fff',
    borderRadius: 28,
    '&:hover': {
      backgroundColor: GREEN,
      backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
      filter: 'brightness(0.95)',
    },
  },
  clientCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid rgba(255, 255, 255, 0.12)`,
  },
  routePhoto: {
    width: '100%',
    height: 170,
    objectFit: 'cover',
    borderRadius: 14,
    marginBottom: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  clientAvatar: {
    width: 48,
    height: 48,
    backgroundColor: GREEN,
    backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
    color: '#fff',
    fontSize: '1.25rem',
  },
  clientName: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
  },
  virtualAudioGuideRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  virtualAudioGuideYes: {
    color: GREEN,
    fontSize: '1.25rem',
  },
  virtualAudioGuideNo: {
    color: '#f44336',
    fontSize: '1.25rem',
  },
  routeTypeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '4px 10px',
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 16,
    backgroundColor: ELECTRIC_BLUE,
    backgroundImage: `linear-gradient(135deg, ${ELECTRIC_BLUE} 0%, ${DARK_ELECTRIC_BLUE} 100%)`,
    border: '1px solid rgba(94, 230, 255, 0.1)',
    color: '#fff',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  routeTypeIcon: {
    width: 20,
    height: 20,
    objectFit: 'contain',
  },
  routeTypeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  metaCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: '1px solid rgba(255, 255, 255, 0.14)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1),
  },
  metaLabel: {
    color: 'rgba(255, 255, 255, 0.68)',
    fontSize: '0.76rem',
    lineHeight: 1.2,
  },
  metaValue: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.95rem',
    lineHeight: 1.35,
  },
  descriptionCard: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.75),
    borderRadius: 14,
    border: '1px solid rgba(255, 255, 255, 0.14)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  discountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  discountInput: {
    flex: '1 1 180px',
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: '#fff',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.35)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255,255,255,0.55)',
      },
      '&.Mui-focused fieldset': {
        borderColor: GREEN,
      },
    },
    '& .MuiInputLabel-outlined': { color: 'rgba(255,255,255,0.7)' },
  },
  discountError: {
    color: '#f44336',
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  checkoutSticky: {
    position: 'sticky',
    bottom: 0,
    marginLeft: -theme.spacing(3),
    marginRight: -theme.spacing(3),
    zIndex: 3,
    marginTop: theme.spacing(1),
    padding: `${theme.spacing(1.5)}px ${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
    borderRadius: 16,
    border: '1px solid rgba(255, 255, 255, 0.14)',
    background: 'rgba(8, 26, 27, 0.9)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  // Sticky bar taller than the viewport pins its top off-screen; unstick while the payment form is open.
  checkoutUnstuck: {
    position: 'static',
  },
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/** Parse bearer token from URL (hash or query). Used only on this page; not persisted. */
function getTokenFromUrl() {
  const hash = window.location.hash.slice(1);
  const hashParams = new URLSearchParams(hash);
  const queryParams = new URLSearchParams(window.location.search);
  const raw =
    hashParams.get('access_token') ||
    hashParams.get('token') ||
    queryParams.get('access_token') ||
    queryParams.get('token') ||
    null;
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.toLowerCase().startsWith('bearer ')) {
    return trimmed.slice(7).trim();
  }
  return trimmed;
}

const BUY_PAGE_TOKEN_KEY = 'route_buy_page_access_token';

/** Get page-scoped token from sessionStorage (used only on this page, not main app auth). */
function getStoredPageToken() {
  try {
    return sessionStorage.getItem(BUY_PAGE_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredPageToken(value) {
  try {
    if (value) sessionStorage.setItem(BUY_PAGE_TOKEN_KEY, value);
    else sessionStorage.removeItem(BUY_PAGE_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/** Remove token from URL (hash and search) for security. */
function stripTokenFromUrl(history, location) {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.slice(1));
  let changed = false;
  if (searchParams.has('access_token') || searchParams.has('token')) {
    searchParams.delete('access_token');
    searchParams.delete('token');
    changed = true;
  }
  if (hashParams.has('access_token') || hashParams.has('token')) {
    hashParams.delete('access_token');
    hashParams.delete('token');
    const newHash = hashParams.toString() ? `#${hashParams.toString()}` : '';
    if (newHash !== location.hash) changed = true;
    const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
    history.replace({ pathname: location.pathname, search, hash: newHash });
    return;
  }
  if (changed) {
    const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
    history.replace({
      pathname: location.pathname,
      search,
      hash: location.hash,
    });
  }
}

const BUY_PAGE_TEXTS = {
  'lt-LT': {
    missingRoute: 'Trūksta maršruto. Naudokite ?clientId=...&routeId=...',
    routeNotFound: 'Maršrutas nerastas.',
    failedToLoadRoute: 'Nepavyko įkelti maršruto.',
    alreadyPurchased: 'Jūs jau įsigijote šį maršrutą.',
    successfullyPurchased: 'Sėkmingai įsigijote šį maršrutą.',
    paymentSetupFailed: 'Mokėjimo paruošimas nepavyko.',
    pleaseSignIn: 'Prisijunkite, kad įsigytumėte.',
    failedToStartPayment: 'Nepavyko pradėti mokėjimo.',
    paymentSuccessful: 'Mokėjimas sėkmingas.',
    buyRoute: 'Pirkti maršrutą',
    navigateBack: 'Grįžti atgal',
    example: 'Pavyzdys:',
    missingReviewAndAudioGuide:
      'Trūksta atsiliepimų ir virtualaus audio gido informacijos.',
    durationHoursUnit: 'val.',
    virtualAudioGuideLabel: 'Virtualus audio gidas',
    routeTypeLabel: 'Maršruto tipas',
    discountCode: 'Nuolaidos kodas',
    applyDiscount: 'Taikyti',
    clearDiscount: 'Pašalinti nuolaidą',
    discountOffLabel: 'nuolaida',
    codeNotFound: 'Kodas nerastas',
    codeInactive: 'Kodas neaktyvus',
    invalidCode: 'Neteisingas kodas',
    usageLimitReached: 'Pasiektas panaudojimų limitas',
    failedToValidateCode: 'Nepavyko patikrinti kodo',
  },
  'en-EU': {
    missingRoute: 'Missing route. Use ?clientId=...&routeId=...',
    routeNotFound: 'Route not found.',
    failedToLoadRoute: 'Failed to load route.',
    alreadyPurchased: 'You have already purchased this route.',
    successfullyPurchased: 'You have successfully purchased this route.',
    paymentSetupFailed: 'Payment setup failed.',
    pleaseSignIn: 'Please sign in to purchase.',
    failedToStartPayment: 'Failed to start payment.',
    paymentSuccessful: 'Payment successful.',
    buyRoute: 'Buy route',
    navigateBack: 'Navigate back',
    example: 'Example:',
    missingReviewAndAudioGuide:
      'Missing review and virtual audio guide details.',
    durationHoursUnit: 'hrs',
    virtualAudioGuideLabel: 'Virtual audio guide is',
    routeTypeLabel: 'Route type',
    discountCode: 'Discount code',
    applyDiscount: 'Apply',
    clearDiscount: 'Remove discount',
    discountOffLabel: 'off',
    codeNotFound: 'Code not found',
    codeInactive: 'Code is inactive',
    invalidCode: 'Invalid code',
    usageLimitReached: 'Usage limit reached',
    failedToValidateCode: 'Failed to validate code',
  },
};

const localizeDiscountError = (message, t) => {
  if (!message) return t.invalidCode;
  if (message === 'Code not found') return t.codeNotFound;
  if (message === 'Code is inactive') return t.codeInactive;
  if (message === 'Invalid code') return t.invalidCode;
  if (message === 'Usage limit reached') return t.usageLimitReached;
  if (message === 'Failed to validate code') return t.failedToValidateCode;
  return message;
};

const RouteBuyPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const locale = location.pathname.startsWith('/en-EU/') ? 'en-EU' : 'lt-LT';
  const buyPath = `/${locale}/buy`;
  const t = BUY_PAGE_TEXTS[locale] || BUY_PAGE_TEXTS['en-EU'];
  const { enqueueSnackbar } = useSnackbar();
  const {
    tokenValid,
    state: { token },
  } = useAuthContext();

  const query = useQuery();
  const clientId = (
    query.get('clientId') ||
    query.get('client_id') ||
    ''
  ).trim();
  const routeId = (query.get('routeId') || '').trim();
  const paymentComplete = query.get('payment') === 'complete';

  const [clientAccount, setClientAccount] = useState(null);
  const displayClientName =
    (clientAccount?.fullName || clientId || '').trim() || null;
  const clientAvatarUrl = clientAccount?.profileImageUrl
    ? clientAccount.profileImageUrl.startsWith('http')
      ? clientAccount.profileImageUrl
      : `${axiosInstance.defaults.baseURL || ''}${clientAccount.profileImageUrl}`
    : null;
  const clientInitials = displayClientName
    ? displayClientName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
    : clientId
      ? String(clientId).slice(0, 2).toUpperCase()
      : '?';

  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);
  const [paymentJustCompleted, setPaymentJustCompleted] = useState(false);
  const [pageScopedToken, setPageScopedToken] = useState(() =>
    getStoredPageToken(),
  );
  const [urlTokenReadComplete, setUrlTokenReadComplete] = useState(false);
  const [appSchema, setAppSchema] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const checkoutRef = useRef(null);

  // The payment form renders in normal flow (not sticky); bring its top into view when it opens.
  useEffect(() => {
    if (paymentIntent && checkoutRef.current) {
      checkoutRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [paymentIntent]);

  // Token for this page only: from URL, sessionStorage (this page only), or main app auth. Not in main app localStorage.
  const effectiveToken = pageScopedToken || getStoredPageToken() || token;
  const hasSessionForPage = !!effectiveToken;

  const effectiveTokenRef = useRef(effectiveToken);
  effectiveTokenRef.current = effectiveToken;

  // Ensure Authorization header is sent for route and route-purchase API calls (interceptor runs with latest token from ref).
  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use((config) => {
      const requestUrl = config.url || '';
      const isRoutePurchase =
        requestUrl.includes('route-purchases') ||
        requestUrl.includes('route-purchase');
      const isRouteById = /\/api\/routes\/[^/]+/.test(requestUrl);
      const authToken = effectiveTokenRef.current;
      if ((isRoutePurchase || isRouteById) && authToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${authToken}`;
      }
      return config;
    });
    return () => axiosInstance.interceptors.request.eject(interceptor);
  }, []);

  // Read token from URL on mount; store in state and sessionStorage. Mark complete so route fetch runs after token is available.
  useEffect(() => {
    const urlToken = getTokenFromUrl();
    if (urlToken) {
      setPageScopedToken(urlToken);
      setStoredPageToken(urlToken);
    }
    setUrlTokenReadComplete(true);
  }, []);

  // Strip token from URL after we've stored it so the bar doesn't show it; token stays in state + sessionStorage.
  useEffect(() => {
    if (!pageScopedToken && !getStoredPageToken()) return;
    if (getTokenFromUrl()) {
      stripTokenFromUrl(history, location);
    }
  }, [pageScopedToken, history, location]);

  // Clear page-scoped token from sessionStorage when leaving the buy page (not main app auth).
  useEffect(() => {
    return () => setStoredPageToken(null);
  }, []);

  // Fetch account info (full name, avatar) by clientId (user id) for the client card.
  useEffect(() => {
    if (!clientId) {
      setClientAccount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          endpoints.accountInfoByUserId(clientId),
          { headers: { Authorization: `Bearer ${effectiveToken}` } },
        );
        if (!cancelled && data) {
          setClientAccount({
            fullName: data.fullName || null,
            profileImageUrl: data.profileImageUrl || null,
          });
        }
      } catch {
        if (!cancelled) {
          setClientAccount({ fullName: null, profileImageUrl: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, effectiveToken]);

  // Fetch App_Schema from app-version for "Navigate back" button (redirect to app deep link).
  useEffect(() => {
    if (!effectiveToken) return;
    let cancelled = false;
    appVersionClient
      .getAppVersion({ Authorization: `Bearer ${effectiveToken}` })
      .then((res) => {
        if (!cancelled && res?.schema) setAppSchema(res.schema.trim());
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [effectiveToken]);

  const navigateBackUrl = appSchema
    ? appSchema.includes('://')
      ? appSchema
      : `${appSchema}://`
    : null;

  // Full-screen dark background: remove any white padding from body/html when this page is shown.
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevBodyMargin = document.body.style.margin;
    const prevBodyPadding = document.body.style.padding;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = '#081A1B';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.backgroundColor = '#081A1B';
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.body.style.margin = prevBodyMargin;
      document.body.style.padding = prevBodyPadding;
      document.documentElement.style.backgroundColor = prevHtmlBg;
    };
  }, []);

  useEffect(() => {
    if (!paymentComplete) return;
    setAlreadyPurchased(true);
    setPaymentJustCompleted(true);
    setPaymentIntent(null);
    setStoredPageToken(null);
    enqueueSnackbar(t.successfullyPurchased, { variant: 'success' });
    const params = new URLSearchParams(location.search);
    params.delete('payment');
    const search = params.toString() ? `?${params.toString()}` : '';
    history.replace({ pathname: buyPath, search });
  }, [paymentComplete, enqueueSnackbar, history, location.search, buyPath, t]);

  const buildReturnUrl = useCallback(() => {
    const search = location.search || '';
    return `${buyPath}${search}`;
  }, [location.search, buyPath]);

  useEffect(() => {
    if (!routeId) {
      setLoading(false);
      setError(t.missingRoute);
      return;
    }
    if (!urlTokenReadComplete) return;
    if (!effectiveToken) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `${endpoints.routes(routeId)}?locale=${locale}`,
          { headers: { Authorization: `Bearer ${effectiveToken}` } },
        );
        if (!cancelled) {
          setRoute(data);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404 && locale === 'en-EU') {
          try {
            const { data: fallbackData } = await axiosInstance.get(
              `${endpoints.routes(routeId)}?locale=lt-LT`,
              { headers: { Authorization: `Bearer ${effectiveToken}` } },
            );
            if (!cancelled) {
              setRoute(fallbackData);
              setError(null);
            }
            return;
          } catch {
            // fallback failed, show not found below
          }
        }
        const message =
          err.response?.status === 404
            ? t.routeNotFound
            : err.response?.data?.error?.message || t.failedToLoadRoute;
        setError(message);
        setRoute(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeId, urlTokenReadComplete, effectiveToken, locale]);

  useEffect(() => {
    if (!hasSessionForPage || !effectiveToken || !routeId || !route?.Paid)
      return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          endpoints.routePurchaseCanPurchase(routeId),
          { headers: { Authorization: `Bearer ${effectiveToken}` } },
        );

        if (
          !cancelled &&
          data?.canPurchase === false &&
          data?.reason === 'already_purchased'
        ) {
          setAlreadyPurchased(true);
        }
      } catch {
        // Ignore; can-purchase is optional
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasSessionForPage, effectiveToken, routeId, route?.Paid]);

  const handleBuyClick = async () => {
    if (!routeId || !route?.Paid || route.PaidAmount == null) return;

    if (!hasSessionForPage) {
      const returnUrl = encodeURIComponent(buildReturnUrl());
      history.push(`/?returnUrl=${returnUrl}`);
      return;
    }

    if (alreadyPurchased) {
      enqueueSnackbar(t.alreadyPurchased, {
        variant: 'info',
      });
      return;
    }

    const isFreePurchase =
      appliedDiscount &&
      discountCode.trim() &&
      Number(appliedDiscount.discountedAmount) === 0;

    try {
      if (isFreePurchase) {
        const { data } = await axiosInstance.post(
          endpoints.routePurchaseCompleteFreePurchase,
          { routeId, discountCode: discountCode.trim() },
          { headers: { Authorization: `Bearer ${effectiveToken}` } },
        );
        if (data?.routePurchaseId) {
          handlePaymentSuccess();
        } else {
          enqueueSnackbar(t.paymentSetupFailed, { variant: 'error' });
        }
        return;
      }

      const body = { routeId };
      if (appliedDiscount && discountCode.trim()) {
        body.discountCode = discountCode.trim();
      }
      const { data } = await axiosInstance.post(
        endpoints.routePurchaseCreatePayment,
        body,
        { headers: { Authorization: `Bearer ${effectiveToken}` } },
      );
      if (data?.clientSecret) {
        setPaymentIntent({
          clientSecret: data.clientSecret,
          publishableKey:
            data.publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
        });
      } else {
        enqueueSnackbar(t.paymentSetupFailed, { variant: 'error' });
      }
    } catch (err) {
      const message =
        err.response?.data?.error?.message || err.response?.status === 401
          ? t.pleaseSignIn
          : t.failedToStartPayment;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentIntent(null);
    setAlreadyPurchased(true);
    setPaymentJustCompleted(true);
    setStoredPageToken(null);
    enqueueSnackbar(t.successfullyPurchased, { variant: 'success' });
  };

  const handlePaymentCancel = () => {
    setPaymentIntent(null);
  };

  const handleApplyDiscount = async () => {
    const code = (discountCode || '').trim();
    if (!code || !routeId) return;
    setDiscountError(null);
    setDiscountLoading(true);
    try {
      const { data } = await axiosInstance.post(
        endpoints.discountCodeValidate,
        { code, routeId },
        {
          headers: effectiveToken
            ? { Authorization: `Bearer ${effectiveToken}` }
            : {},
        },
      );
      if (data?.valid === true) {
        setAppliedDiscount({
          originalAmount: data.originalAmount,
          discountedAmount: data.discountedAmount,
          discountPercent: data.discountPercent,
        });
        setDiscountError(null);
      } else {
        setAppliedDiscount(null);
        setDiscountError(localizeDiscountError(data?.message, t));
      }
    } catch (err) {
      setAppliedDiscount(null);
      setDiscountError(
        localizeDiscountError(
          err.response?.data?.error?.message || err.response?.data?.message,
          t,
        ),
      );
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleClearDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError(null);
    setDiscountCode('');
  };

  if (loading) {
    return (
      <Box
        className={classes.root}
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={3}
      >
        <CircularProgress style={{ color: GREEN }} />
      </Box>
    );
  }

  if (error || !route) {
    return (
      <Box className={classes.root}>
        <Box className={classes.content}>
          {clientId && (
            <Box className={classes.clientCard}>
              <Avatar
                src={clientAvatarUrl || undefined}
                className={classes.clientAvatar}
              >
                {!clientAvatarUrl ? clientInitials : null}
              </Avatar>
              <Typography className={classes.clientName}>
                {displayClientName || clientId}
              </Typography>
            </Box>
          )}
          <Typography style={{ color: '#f44336' }}>{error}</Typography>
          <Typography
            variant="body2"
            className={classes.textSecondary}
            style={{ marginTop: 8 }}
          >
            {t.example} {buyPath}?clientId=abc&routeId=xyz
          </Typography>
        </Box>
      </Box>
    );
  }

  const isPaid =
    route.Paid === true &&
    route.PaidAmount != null &&
    Number(route.PaidAmount) > 0;
  const showBuyButton = isPaid && !alreadyPurchased && !paymentIntent;

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.contentScrollArea}>
          {clientId && (
            <Box className={classes.clientCard}>
              <Avatar
                src={clientAvatarUrl || undefined}
                className={classes.clientAvatar}
              >
                {!clientAvatarUrl ? clientInitials : null}
              </Avatar>
              <Typography className={classes.clientName}>
                {displayClientName || clientId}
              </Typography>
            </Box>
          )}
          <Box className={classes.infoCard}>
            {route.Images?.length > 0 && (
              <Box
                component="img"
                className={classes.routePhoto}
                src={
                  route.Images[0].url?.startsWith('http')
                    ? route.Images[0].url
                    : `${axiosInstance.defaults.baseURL || ''}${route.Images[0].url || ''}`
                }
                alt={route.Images[0].name || route.Title || ''}
              />
            )}
            <Typography variant="h5" className={classes.section}>
              {route.Title}
            </Typography>
            {route.TravelMethod && (
              <Box className={classes.routeTypeRow}>
                <span className={classes.routeTypeBadge}>
                  {TRAVEL_METHOD_ICONS[route.TravelMethod] && (
                    <img
                      src={`/route-types-icons/${TRAVEL_METHOD_ICONS[route.TravelMethod]}`}
                      alt=""
                      className={classes.routeTypeIcon}
                    />
                  )}
                  {TRAVEL_METHOD_LABELS[locale]?.[route.TravelMethod] ??
                    route.TravelMethod}
                </span>
              </Box>
            )}
            <Box className={classes.metaCard}>
              <Box className={classes.metaGrid}>
                <Box>
                  <Typography className={classes.metaLabel}>
                    Trukmė ({t.durationHoursUnit})
                  </Typography>
                  <Typography className={classes.metaValue}>
                    {route.Duration != null && route.Duration !== ''
                      ? route.Duration
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography className={classes.metaLabel}>
                    Atstumas (km)
                  </Typography>
                  <Typography className={classes.metaValue}>
                    {route.Length != null ? route.Length : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography className={classes.metaLabel}>
                    {t.virtualAudioGuideLabel}
                  </Typography>
                  <Box className={classes.virtualAudioGuideRow}>
                    {route.VirtualAudioGuideExists ? (
                      <CheckCircleIcon
                        className={classes.virtualAudioGuideYes}
                      />
                    ) : (
                      <CancelIcon className={classes.virtualAudioGuideNo} />
                    )}
                    <Typography className={classes.metaValue}>
                      {route.VirtualAudioGuideExists ? 'Yra' : 'Nėra'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography className={classes.metaLabel}>
                    Regionas
                  </Typography>
                  <Typography className={classes.metaValue}>
                    {[getRegionLabel(route.Region), route.District]
                      .filter(Boolean)
                      .join(' · ') || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className={classes.descriptionCard}>
              <Typography variant="body2" className={classes.section}>
                {route.Description}
              </Typography>
            </Box>
          </Box>
        </Box>
        {isPaid && (
          <div
            ref={checkoutRef}
            className={`${classes.checkoutSticky}${paymentIntent ? ` ${classes.checkoutUnstuck}` : ''}`}
          >
            <Typography className={classes.price}>
              {appliedDiscount ? (
                <>
                  <span
                    style={{ textDecoration: 'line-through', marginRight: 8 }}
                  >
                    €{Number(appliedDiscount.originalAmount).toFixed(2)}
                  </span>
                  €{Number(appliedDiscount.discountedAmount).toFixed(2)}
                  {appliedDiscount.discountPercent > 0 && (
                    <span
                      className={classes.textSecondary}
                      style={{ marginLeft: 8, fontSize: '0.9rem' }}
                    >
                      ({appliedDiscount.discountPercent}% {t.discountOffLabel})
                    </span>
                  )}
                </>
              ) : (
                <>€{Number(route.PaidAmount).toFixed(2)}</>
              )}
            </Typography>
            {!alreadyPurchased && !paymentIntent && (
              <Box className={classes.discountRow}>
                <TextField
                  className={classes.discountInput}
                  size="small"
                  variant="outlined"
                  label={t.discountCode}
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value);
                    setDiscountError(null);
                  }}
                  disabled={discountLoading}
                  InputProps={{
                    style: { color: '#fff' },
                  }}
                  InputLabelProps={{
                    style: { color: 'rgba(255,255,255,0.7)' },
                  }}
                />
                <Button
                  className={classes.discountApplyButton}
                  variant="contained"
                  size="small"
                  onClick={handleApplyDiscount}
                  disabled={discountLoading || !discountCode.trim()}
                >
                  {discountLoading ? '...' : t.applyDiscount}
                </Button>
                {appliedDiscount && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearDiscount}
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    {t.clearDiscount}
                  </Button>
                )}
              </Box>
            )}
            {discountError && (
              <Typography className={classes.discountError}>
                {discountError}
              </Typography>
            )}
            {showBuyButton && (
              <Button
                className={classes.buyButton}
                variant="contained"
                onClick={handleBuyClick}
              >
                {t.buyRoute}
              </Button>
            )}
            {paymentIntent && (
              <StripePaymentForm
                clientSecret={paymentIntent.clientSecret}
                publishableKey={paymentIntent.publishableKey}
                buyPath={buyPath}
                locale={locale}
                returnUrlSearch={location.search}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            )}
          </div>
        )}

        {alreadyPurchased && (
          <>
            <Typography className={classes.alreadyPurchased}>
              {paymentJustCompleted
                ? t.successfullyPurchased
                : t.alreadyPurchased}
            </Typography>
            {navigateBackUrl && (
              <Button
                className={classes.navigateBackButton}
                variant="contained"
                component="a"
                href={navigateBackUrl}
              >
                {t.navigateBack}
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default RouteBuyPage;
